import { Button, Dialog, Group, MantineProvider, Progress, Text } from "@mantine/core";
import React, {
  createContext, useContext, useMemo
} from "react";
import { Socket, connect } from 'socket.io-client';
import { useAuth } from "./auth.context";

interface ConfirmDialogProps{
  (
    props : {text:string, buttonText?:string},
    callback: (props: { confirmed: boolean }) => void
  ) : void
}
interface AppContextProps {
  confirmDialog: ConfirmDialogProps;
  setColorScheme: (colorScheme : "dark" | "light") => void;
  socket: Socket;
}

const AppContext = createContext<AppContextProps>({} as AppContextProps);

export const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const lsColorScheme = localStorage.getItem("@satify-admin/colorScheme") as ("light" | "dark");
  
  const [openedConfirm, setOpenedConfirm] = React.useState(null);
  const [importingFiles, setImportingFiles] = React.useState([]);
  const [agents, setAgents] = React.useState<any[]>([]);
  const [colorScheme, setColorScheme] = React.useState<"dark" | "light">(lsColorScheme || "light");
  const [registration, setRegistration] = React.useState<ServiceWorkerRegistration>(null);

  const [socket, setSocket] = React.useState<Socket>(null);
  
  const { user: currentUser } = useAuth();

  const confirmDialog : ConfirmDialogProps = ({text, buttonText = "Confirmar"}, callback) => {
    setOpenedConfirm({text, buttonText, callback})
  }
  React.useEffect(() => {
    if(currentUser){
      const localUser = localStorage.getItem("@satify-admin/user");
      const user = localUser ? JSON.parse(localUser) : null;
      const s = connect(process.env.REACT_APP_SERVER_URL, {extraHeaders: {
        Authorization: `Bearer ${user.token}`
      }})
      setSocket(s);
    }
  }, [currentUser]);

  const value = useMemo<AppContextProps>(() => {
    return { confirmDialog, setColorScheme, socket, registration, agents };
  }, [confirmDialog, setColorScheme, socket, registration, agents]);

  return <MantineProvider
    withGlobalStyles
    withNormalizeCSS
    theme={{
      colorScheme,
      loader: 'dots',
      fontFamily: 'Montserrat, sans-serif',
      datesLocale: "pt-br",
      colors: {
        brand: ['#7AD1DD', '#5FCCDB', '#44CADC', '#028de0', '#028de0', '#028de0', '#028de0', '#028de0', '#028de0', '#028de0'],
        white: ['#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF', '#EFEFEF', '#FFFFFF', '#FFFFFF'],
        purple: ['#7950F1'],
        darkpurple: ['#392086']
      },
      headings: {
        fontWeight: "bold",
      },
      primaryColor: 'orange'
    }}
  >
    <AppContext.Provider value={value}>
      <>        
        <>
          <Dialog
            opened={Boolean(openedConfirm)}
            withCloseButton
            onClose={() => setOpenedConfirm(null)}
            size="lg"
            radius="md"
            position={{top: 10, right: 10}}
          >
            <Text size="sm" style={{ marginBottom: 10 }} weight={500}>
              {openedConfirm?.text}
            </Text>

            <Group align="flex-end">
              <Button onClick={() => {
                openedConfirm?.callback && openedConfirm?.callback({confirmed: true});
                setOpenedConfirm(null);
              }}>{openedConfirm?.buttonText}</Button>
            </Group>
          </Dialog>
        </>
    </>
    {children}
    <Dialog
      opened={importingFiles.length > 0}
    >
        {importingFiles.map(imp => <div>
          <div style={{textAlign: 'right'}}>
            <Text color="orange" size="sm">{imp.done}/{imp.total}</Text>
          </div>
          <Progress color="orange" value={imp.percent}  />
          <Text size="xs" style={{textAlign: 'right'}}>{{themes: "Importação de pautas"}[imp.type]} - {imp.origin.toUpperCase()}</Text>
        </div>)}
    </Dialog>
    </AppContext.Provider>
  </MantineProvider>
};

export const useApp = () => useContext(AppContext);
